import { TabsBar } from '@/modules/common/components/tabs-bar';
import styles from './index.module.sass';
import { SpecItem } from '../spec-item';
import { Sorter } from '@/modules/common/components/sorter';
import { MixesList } from '../mixes-list';
import { useStore } from 'effector-react';
import {
  $markeredMixes,
  $mixesSort,
  $selectedRecipeStore,
  setMarkeredMixesToInitialState,
  setMixesSortOrder,
  setMixSortValue,
} from '@/modules/mixing/store/recipes.store';
import { useEffect, useState } from 'react';
import {
  $topRecipesForWarehouse,
  fetchTopRecipesForWarehouse,
} from '@/widgets/recipe/model';
import { IExclusiveRecipe } from '@/widgets/recipe/types';
import { RubleRegular } from '@/modules/common/components/ruble-regular';

export const MixesTab = ({
  selectRecipeFoo,
  tabs,
  activeTab,
  setActiveTab,
}: {
  selectRecipeFoo: (id: number) => void;
  tabs: {
    label: string;
    id: number;
    disabled?: boolean;
  }[];
  activeTab: number;
  setActiveTab: (val: number) => void;
}) => {
  const mixesSort = useStore($mixesSort);
  const topRecipes = useStore($topRecipesForWarehouse);
  const [sortedData, setSortedData] = useState<IExclusiveRecipe[]>([]);
  const activeWh = useStore($selectedRecipeStore);

  useEffect(() => {
    return () => setMarkeredMixesToInitialState();
  }, []);

  useEffect(() => {
    activeWh && fetchTopRecipesForWarehouse(Number(activeWh.id));
  }, []);

  useEffect(() => {
    const sortedDataCopy = topRecipes?.mixes.slice();
    let compareFoo;
    const sortBy = mixesSort.sortBy.find((item) => item.active);
    if (sortBy && sortBy.val === 'profit') {
      mixesSort.order === 'asc'
        ? (compareFoo = (a: IExclusiveRecipe, b: IExclusiveRecipe) =>
            a.mixing_profit - b.mixing_profit)
        : (compareFoo = (a: IExclusiveRecipe, b: IExclusiveRecipe) =>
            b.mixing_profit - a.mixing_profit);
    } else if (sortBy && sortBy.val === 'priceLogistic') {
      mixesSort.order === 'asc'
        ? (compareFoo = (a: IExclusiveRecipe, b: IExclusiveRecipe) =>
            a.price_logistic - b.price_logistic)
        : (compareFoo = (a: IExclusiveRecipe, b: IExclusiveRecipe) =>
            b.price_logistic - a.price_logistic);
    } else if (sortBy && sortBy.val === 'priceTotal') {
      mixesSort.order === 'asc'
        ? (compareFoo = (a: IExclusiveRecipe, b: IExclusiveRecipe) =>
            a.price_all - b.price_all)
        : (compareFoo = (a: IExclusiveRecipe, b: IExclusiveRecipe) =>
            b.price_all - a.price_all);
    } else if (sortBy && sortBy.val === 'priceTon') {
      mixesSort.order === 'asc'
        ? (compareFoo = (a: IExclusiveRecipe, b: IExclusiveRecipe) =>
            a.price_per_ton - b.price_per_ton)
        : (compareFoo = (a: IExclusiveRecipe, b: IExclusiveRecipe) =>
            b.price_per_ton - a.price_per_ton);
    } else if (sortBy && sortBy.val === 'weight') {
      mixesSort.order === 'asc'
        ? (compareFoo = (a: IExclusiveRecipe, b: IExclusiveRecipe) =>
            a.weight - b.weight)
        : (compareFoo = (a: IExclusiveRecipe, b: IExclusiveRecipe) =>
            b.weight - a.weight);
    } else if (sortBy && sortBy.val === 'buyer') {
      mixesSort.order === 'asc'
        ? (compareFoo = (a: IExclusiveRecipe, b: IExclusiveRecipe) =>
            a.buyer_company.localeCompare(b.buyer_company, 'ru', {
              sensitivity: 'base',
            }))
        : (compareFoo = (a: IExclusiveRecipe, b: IExclusiveRecipe) =>
            b.buyer_company.localeCompare(a.buyer_company, 'ru', {
              sensitivity: 'base',
            }));
    }
    if (!compareFoo && topRecipes) return setSortedData(topRecipes?.mixes);
    const res = sortedDataCopy?.sort(compareFoo);
    res && setSortedData(res);
  }, [mixesSort.order, mixesSort.sortBy, topRecipes]);

  return (
    <>
      <div className={styles.content}>
        <TabsBar tabs={tabs} activeItem={activeTab} setActiveTab={setActiveTab} />
        <div className={styles.specs}>
          <SpecItem
            label="общий вес миксов"
            value={<>{(topRecipes?.mixes_total?.total_mix_weight || 0).toLocaleString('RU-ru', { maximumFractionDigits: 1 })} <RubleRegular customSymbol='т'/></>}
          />
          <SpecItem
            label="общая стоимость миксов"
            value={<>{(topRecipes?.mixes_total?.total_mix_price || 0).toLocaleString('RU-ru', { maximumFractionDigits: 0 })} <RubleRegular customSymbol='₽'/></>}
          />
          <SpecItem
            label="общая стоимость логистики миксов"
            value={<>{(topRecipes?.mixes_total?.total_mix_price_logistic || 0).toLocaleString('RU-ru', { maximumFractionDigits: 0 })} <RubleRegular customSymbol='₽'/></>}
          />
          <SpecItem
            label="стоимость всех миксов с учетом логистики"
            value={<>{(topRecipes?.mixes_total?.total_mix_price_netto || 0).toLocaleString('RU-ru', { maximumFractionDigits: 0 })} <RubleRegular customSymbol='₽'/></>}
          />
          <div className={styles.profitBadge}>
            <span className={styles.label}>общая выгода от миксования</span>
            <span className={styles.profit}>
              {(
                topRecipes?.total_profit || 0
              ).toLocaleString('RU-ru', { maximumFractionDigits: 0 })}{' '}
              <RubleRegular p={0}/>
            </span>
          </div>
        </div>
        <Sorter
          sortOption={mixesSort.sortBy}
          setSortOption={setMixSortValue}
          orderBy={mixesSort.order as 'asc' | 'desc'}
          setOrderBy={setMixesSortOrder}
        />
        <MixesList selectRecipeFoo={selectRecipeFoo} mixesList={sortedData} />
      </div>
    </>
  );
};
